import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { BOLAG, formatSEK } from "@/lib/portfolio-data";
import { addSvar, findMottagareByToken } from "@/lib/reports-store";
import { CheckCircle2, Sparkles } from "lucide-react";

export const Route = createFileRoute("/rapport/$token")({
  head: () => ({
    meta: [
      { title: "Inkubationsrapport — Inkubera" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: RapportForm,
});

function RapportForm() {
  const { token } = Route.useParams();
  const mottagare = useMemo(() => findMottagareByToken(token), [token]);
  const bolag = useMemo(
    () => (mottagare ? BOLAG.find((b) => b.id === mottagare.bolagId) : undefined),
    [mottagare],
  );

  const [skickat, setSkickat] = useState(false);
  const [form, setForm] = useState({
    antalPersoner: "",
    antalAktieagare: "",
    finansiellaStod: "",
    krediter: "",
    agarkapitalEmissioner: "",
    agarkapitalKonvertibler: "",
    kommentar: "",
  });

  if (!mottagare || !bolag) {
    return (
      <Shell>
        <div className="rounded-2xl border border-border bg-card p-10 text-center">
          <h1 className="font-display text-2xl mb-2">Länken är inte giltig</h1>
          <p className="text-muted-foreground">
            Kontakta din affärsutvecklare på Inkubera för en ny länk.
          </p>
        </div>
      </Shell>
    );
  }

  const totalt =
    num(form.finansiellaStod) +
    num(form.krediter) +
    num(form.agarkapitalEmissioner) +
    num(form.agarkapitalKonvertibler);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    addSvar({
      bolagId: bolag.id,
      bolagNamn: bolag.namn,
      antalPersoner: num(form.antalPersoner),
      antalAktieagare: num(form.antalAktieagare),
      finansiellaStod: num(form.finansiellaStod),
      krediter: num(form.krediter),
      agarkapitalEmissioner: num(form.agarkapitalEmissioner),
      agarkapitalKonvertibler: num(form.agarkapitalKonvertibler),
      kommentar: form.kommentar || undefined,
    });
    setSkickat(true);
  };

  if (skickat) {
    return (
      <Shell>
        <div className="rounded-2xl border border-border bg-card p-10 text-center">
          <CheckCircle2 className="size-12 mx-auto text-primary mb-4" />
          <h1 className="font-display text-2xl mb-2">Tack för din rapport!</h1>
          <p className="text-muted-foreground">
            Informationen har registrerats i Inkuberas plattform.
          </p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-2">
          Inkubationsrapport · {bolag.namn}
        </div>
        <h1 className="font-display text-3xl">Status för perioden</h1>
        <p className="text-muted-foreground mt-2">
          Fyll i nuvarande siffror för bolaget. Ungefärliga belopp i SEK räcker bra.
        </p>
      </div>

      <form onSubmit={onSubmit} className="space-y-6">
        <Card title="Organisation">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Antal personer verksamma i bolaget">
              <NumInput value={form.antalPersoner} onChange={(v) => setForm({ ...form, antalPersoner: v })} />
            </Field>
            <Field label="Antal aktieägare">
              <NumInput value={form.antalAktieagare} onChange={(v) => setForm({ ...form, antalAktieagare: v })} />
            </Field>
          </div>
        </Card>

        <Card
          title="Totalt kapital tillfört bolaget"
          right={<span className="text-sm text-muted-foreground">Summa: <span className="font-medium text-foreground">{formatSEK(totalt)}</span></span>}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Finansiella stöd (bidrag, Vinnova, EU m.m.)">
              <SekInput value={form.finansiellaStod} onChange={(v) => setForm({ ...form, finansiellaStod: v })} />
            </Field>
            <Field label="Krediter (lån, kreditiv)">
              <SekInput value={form.krediter} onChange={(v) => setForm({ ...form, krediter: v })} />
            </Field>
            <Field label="Ägarkapital — emissioner">
              <SekInput value={form.agarkapitalEmissioner} onChange={(v) => setForm({ ...form, agarkapitalEmissioner: v })} />
            </Field>
            <Field label="Ägarkapital — konvertibler / konvertibla lån">
              <SekInput value={form.agarkapitalKonvertibler} onChange={(v) => setForm({ ...form, agarkapitalKonvertibler: v })} />
            </Field>
          </div>
        </Card>

        <Card title="Kommentar (valfritt)">
          <textarea
            value={form.kommentar}
            onChange={(e) => setForm({ ...form, kommentar: e.target.value })}
            rows={4}
            placeholder="Större händelser, milstolpar eller utmaningar under perioden…"
            className="w-full rounded-lg bg-secondary p-3 text-sm border border-transparent focus:border-ring focus:outline-none resize-none"
          />
        </Card>

        <div className="flex justify-end">
          <button
            type="submit"
            className="h-11 px-6 rounded-lg bg-foreground text-background font-medium hover:opacity-90"
          >
            Skicka rapport
          </button>
        </div>
      </form>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center gap-2.5">
          <div className="size-9 rounded-xl bg-foreground flex items-center justify-center text-background">
            <Sparkles className="size-4" strokeWidth={2.5} />
          </div>
          <div className="font-display text-lg">Inkubera</div>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-10">{children}</main>
      <footer className="max-w-3xl mx-auto px-6 pb-10 text-xs text-muted-foreground">
        <Link to="/" className="hover:text-foreground">Inkubera Portfolio Intelligence</Link>
      </footer>
    </div>
  );
}

function Card({ title, right, children }: { title: string; right?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-border bg-card p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-lg">{title}</h2>
        {right}
      </div>
      {children}
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-sm text-muted-foreground mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}

function NumInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <input
      type="number"
      min={0}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-10 rounded-lg bg-secondary px-3 text-sm border border-transparent focus:border-ring focus:outline-none"
    />
  );
}

function SekInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="relative">
      <input
        type="number"
        min={0}
        step={1000}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-10 rounded-lg bg-secondary pl-3 pr-12 text-sm border border-transparent focus:border-ring focus:outline-none"
      />
      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">SEK</span>
    </div>
  );
}

function num(v: string) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}
